import React, { useCallback, useState, useEffect, useRef } from 'react';

import { Theme, Box, Button, Typography } from '@material-ui/core';
import { withStyles, WithStyles, createStyles, makeStyles } from '@material-ui/core/styles';

import { LiveChatNotificationResponseNext } from '@alme/types/response/LiveChatNotificationResponse';

import liveChatControlsStyles from './AlmeLiveChatControls.styles';
import { FocusComponentProps } from '../../../models/ComponentProps';
import { AlmeResponseNext } from '@alme/types/response/AlmeResponse';
import { AlmeApiUtils } from '../../../api/AlmeApiUtils';
import { AlmeLiveChatControlsConfig } from './AlmeLiveChatControlsConfig';
import AlmeEndLiveChatDialog from './AlmeEndLiveChatDialog';

const useStyles = makeStyles(({ spacing }: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexWrap: 'nowrap',
            marginBottom: `-${spacing(1)}px`,
            alignItems: 'center',
        },
        endChatButton: {
        },
        typingNotification: {
            padding: `${spacing(.75)}px ${spacing(1)}`,
            flexGrow: 1,
            textAlign: 'right',
            marginRight: spacing(1),
        },
    }),
);

function HandleIsDialogOpen(props: AlmeLiveChatControlsProps, shouldFocus: boolean,
    closeRef: React.RefObject<HTMLButtonElement>, setShouldFocus: React.Dispatch<React.SetStateAction<boolean>>): void {
    useEffect(() => {
        if (!props.isDialogOpen && shouldFocus) {
            closeRef.current?.focus();
            setShouldFocus(false);
        }
    }, [props.isDialogOpen, shouldFocus, setShouldFocus, closeRef]);
}

function OperatorTyping(props: AlmeLiveChatControlsProps, typingMessage: React.MutableRefObject<string>, isTyping: boolean,
    typingId: number, setIsTyping: React.Dispatch<React.SetStateAction<boolean>>, setTypingId: React.Dispatch<React.SetStateAction<number>>): void {
    useEffect(() => {
        if ((props.response as LiveChatNotificationResponseNext)?.extensionData?.MessageOrigin === 'Operator-Typing') {
            const operatorName = (props.response as LiveChatNotificationResponseNext)?.extensionData?.OperatorData?.Name || props.almeConfig.liveChatControls.defaultOperatorName;
            typingMessage.current = `${operatorName} ${props.almeConfig.liveChatControls.typingMessageText}`;
            if (!isTyping && typingId < props.responseId) {
                setIsTyping(true);
                setTypingId(props.responseId);
            }
        }
    }, [isTyping, props.almeConfig.liveChatControls.defaultOperatorName, props.almeConfig.liveChatControls.typingMessageText, props.response, props.responseId, setIsTyping, setTypingId, typingId, typingMessage]);
}

interface AlmeLiveChatControlsProps extends WithStyles<typeof liveChatControlsStyles> {
    almeConfig: AlmeLiveChatControlsConfig;
    isLiveChatConnected: boolean;
    onEndChat: () => void;
    setIsDialogOpen: (isOpen: boolean) => void;
    responseId: number;
    response?: LiveChatNotificationResponseNext | AlmeResponseNext;
    focusProps?: FocusComponentProps;
    isDialogOpen?: boolean;
}

const AlmeLiveChatControls: React.FC<AlmeLiveChatControlsProps> = (props: AlmeLiveChatControlsProps) => {
    const classes = useStyles();
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [typingId, setTypingId] = useState<number>(0);
    const typingMessage = useRef<string>('');
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [shouldFocus, setShouldFocus] = useState<boolean>(false);
    const closeRef = useRef<HTMLButtonElement>(null);

    HandleIsDialogOpen(props, shouldFocus, closeRef, setShouldFocus);

    const handleEndChatClick = () => {
        setShowDialog(true);
    };

    const handleCancelDialogClick = () => {
        setShowDialog(false);
        setShouldFocus(true);
    };

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (isTyping) {
            timer = setTimeout(() => {
                setIsTyping(false);
            }, props.almeConfig.liveChatControls.typingMessageDelay);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [isTyping, props.almeConfig.liveChatControls.typingMessageDelay]);

    OperatorTyping(props, typingMessage, isTyping, typingId, setIsTyping, setTypingId);

    if (!props.isLiveChatConnected) {
        return null;
    }

    return (
        <Box className={classes.root}>
            <Button id='alme-live-chat-end-button' className={classes.endChatButton} onClick={handleEndChatClick}
                {...props.focusProps} ref={closeRef}>{props.almeConfig.liveChatControls.buttonText}</Button>
            {isTyping &&
                <Typography id='alme-live-chat-typing' className={`${classes.typingNotification} ${props.classes.typingNotification} `}>
                    {typingMessage.current}<span>.</span><span>.</span><span>.</span>
                </Typography>
            }
            <AlmeEndLiveChatDialog isOpen={showDialog} almeConfig={props.almeConfig} setIsDialogOpen={props.setIsDialogOpen} onCancelClick={handleCancelDialogClick} onEndClick={props.onEndChat}></AlmeEndLiveChatDialog>
        </Box>
    );
};

export default withStyles(liveChatControlsStyles)(AlmeLiveChatControls);

export function OnEndChat(apiUtils: React.MutableRefObject<AlmeApiUtils>): () => void {
    return useCallback((): void => {
        /**
         * If the user is in a live chat session, whether waiting in a queue or actively talking with one or more operators,
         *  this event will terminate the session. The user will subsequently be deescalated back to the IVA.
         */
        void apiUtils.current.submitReportingEvent('TerminateSession', 'User');
    }, [apiUtils]);
}
